import { DiagramAction, RealAction } from '../enum/enum';
import { laneInterChanged, swimLaneMeasureAndArrange, updateHeaderMaxWidth, updatePhaseMaxWidth, updateSwimLaneObject } from '../utility/swim-lane-util';
import { findAnnotation } from '../utility/diagram-util';
import { cloneObject } from '../utility/base-util';
/**
 * Provides collaboration functionality for the Syncfusion Diagram component.
 * The `DiagramCollaboration` class enables real-time collaborative editing by serializing
 * local diagram changes into JSON payloads that can be transmitted to remote users,
 * and by applying incoming remote changes to the local diagram instance.
 * **Key Features:**
 * - Serializes history entries (undo/redo actions) into collaboration-friendly JSON format
 * - Applies remote diagram changes while maintaining consistency
 * - Handles grouped operations for batch updates
 * - Supports various change types: position, size, rotation, collections, properties, etc.
 * **Typical Usage:**
 * 1. Hook into the diagram's history change events
 * 2. Call `serializeHistoryChange()` to get JSON payloads for broadcasting
 * 3. Receive remote changes and apply via `applyRemoteChanges()`
 * @private
 */
export class DiagramCollaboration {
    /**
     * Constructor for the collaborative module
     *
     * @private
     */
    constructor() {
        /**
         * Buffer for accumulating serialized changes during grouped operations.
         * Cleared when a group action completes.
         * @private
         */
        this.collaborativeCollection = [];
        /**
         * Indicates whether changes are currently being accumulated as part of a group action.
         * Set to true when a StartGroup entry is detected, false when EndGroup completes.
         * @private
         */
        this.isGroupAction = false;
        //constructs the collaborative module
    }
    /**
     * Serializes diagram changes for collaboration.
     * Serializes the given history change arguments into JSON payloads
     * that can be sent to remote collaborators.
     * @param {IHistoryChangeArgs} args - The history change event arguments containing change details.
     * @param {Diagram} diagram - The diagram instance from which the change originated.
     * @returns {string[]} An array of serialized diagram change JSON strings to send to collaborators.
     * @private
     */
    serializeHistoryChange(args, diagram) {
        if (!args) {
            return [];
        }
        const historyEntry = args.change;
        if (!historyEntry) {
            return [];
        }
        const isUndo = args.action === 'Undo';
        const isStartGroup = historyEntry.type === (isUndo ? 'EndGroup' : 'StartGroup');
        const isEndGroup = historyEntry.type === (isUndo ? 'StartGroup' : 'EndGroup');
        if (historyEntry.type === 'EndGroup') {
            diagram.groupActionDepth += isUndo ? 1 : -1;
        }
        else if (historyEntry.type === 'StartGroup') {
            diagram.groupActionDepth += isUndo ? -1 : 1;
        }
        if (isStartGroup && (diagram.groupActionDepth === 1)) {
            this.collaborativeCollection = [];
            this.isGroupAction = true;
        }
        // 2. Determine the full list of objects affected by the change.
        const changedObjects = this.getChangedObjects(args, args.undoObject, args.redoObject, diagram);
        // 3. Categorize objects and enrich them with additional info
        const { nodes: nodes, connectors: connectors } = this.categorizeAndEnrichObjects(changedObjects, args);
        const propertyChanges = this.processHistoryEntryType(args, args.undoObject, args.redoObject, historyEntry, diagram);
        // 5. Check if there's any meaningful change to send
        const hasChanges = nodes.length > 0 || connectors.length > 0 || propertyChanges != null;
        if (hasChanges) {
            historyEntry.childTable = args.childTable;
            if (args.childTable) {
                for (const node in args.childTable) {
                    if (Object.prototype.hasOwnProperty.call(args.childTable, node)) {
                        historyEntry.childTable[`${node}`] = cloneObject(diagram.getNodeObject(node));
                    }
                }
            }
            // 6. Create the payload and serialize
            const value = {
                modifiedNodes: nodes, modifiedConnectors: connectors,
                entryType: historyEntry.type,
                collectionChangedAction: historyEntry.changeType,
                actionTrigger: args.action,
                propertyChanges: propertyChanges,
                historyAction: historyEntry.historyAction,
                entry: historyEntry
            };
            const json = JSON.stringify(value);
            this.collaborativeCollection.push(json);
        }
        if ((isEndGroup || !this.isGroupAction) && diagram.groupActionDepth === 0) {
            const historyEntries = [...this.collaborativeCollection];
            this.collaborativeCollection = [];
            this.isGroupAction = false;
            return historyEntries;
        }
        return [];
    }
    /**
     * Processes a history entry and extracts property change metadata.
     * Examines the history entry type and constructs a `PropertyChangedAction` object
     * containing relevant old/new values, object IDs, and child references needed
     * for remote clients to apply the same change.
     * @param {IHistoryChangeArgs} args - The history change event arguments.
     * @param {NodeModel | ConnectorModel | SelectorModel | DiagramModel | ShapeAnnotationModel | PathAnnotationModel | PointPortModel} oldValue - The object state before the change.
     * @param {NodeModel | ConnectorModel | SelectorModel | DiagramModel} newValue - The object state after the change.
     * @param {HistoryEntry} historyEntry - The history entry describing the change type.
     * @param {Diagram} diagram - The diagram instance.
     * @returns {PropertyChangedAction} Metadata describing the property changes, or null if not applicable.
     * @private
     */
    processHistoryEntryType(args, oldValue, newValue, historyEntry, diagram) {
        let action = null;
        switch (historyEntry.type) {
            case 'LabelCollectionChanged':
            case 'PortCollectionChanged':
            case 'PhaseCollectionChanged':
            case 'LaneCollectionChanged': {
                action = { newObjectValue: oldValue };
                break;
            }
            case 'LanePositionChanged': {
                action = args.action === 'Undo' ? { newObjectValue: oldValue } : args.action === 'Redo' ? { newObjectValue: newValue }
                    : { newObjectValue: args.source[0] };
                break;
            }
            case 'PropertyChanged': {
                if (newValue.nodes) {
                    for (const node in newValue.nodes) {
                        if (Object.prototype.hasOwnProperty.call(newValue.nodes, node)) {
                            const obj = newValue.nodes[`${node}`];
                            if (obj.style && obj.style.gradient) {
                                this.cloneGradientStops(obj.style.gradient);
                            }
                            const nodechildrenId = this.getChildrenIds(node, obj, diagram, true);
                            action = { childrenId: nodechildrenId };
                        }
                    }
                }
                if (oldValue.connectors) {
                    for (const connector in oldValue.connectors) {
                        if (Object.prototype.hasOwnProperty.call(oldValue.connectors, connector)) {
                            const obj = oldValue.connectors[`${connector}`];
                            this.cloneAsObject(obj);
                        }
                    }
                }
                if (newValue.connectors) {
                    for (const connector in newValue.connectors) {
                        if (Object.prototype.hasOwnProperty.call(newValue.connectors, connector)) {
                            const obj = newValue.connectors[`${connector}`];
                            this.cloneAsObject(obj);
                            const nodechildrenId = this.getChildrenIds(connector, obj, diagram, false);
                            action = { childrenId: nodechildrenId };
                        }
                    }
                }
                action = Object.assign({}, action, { sourceIds: args.sourceId, oldValue: { nodes: oldValue.nodes, connectors: oldValue.connectors }, newValue: { nodes: newValue.nodes, connectors: newValue.connectors } });
                break;
            }
            case 'AddChildToGroupNode':
            case 'RemoveChildFromGroupNode':
            case 'AnnotationPropertyChanged': {
                action = { objectId: args.objectId };
                break;
            }
        }
        return action;
    }
    /**
     * Extracts child IDs (annotations and ports) from a node or connector.
     * Used to build a list of nested element IDs that need to be synchronized
     * during property change operations.
     * @param {string} index - The index key of the object in the diagram's nodes/connectors collection.
     * @param {NodeModel | ConnectorModel} obj - The node or connector model.
     * @param {Diagram} diagram - The diagram instance containing the object.
     * @param {boolean} isNode - Represets the object is node.
     * @returns {string[]} An array of child element IDs (annotations and ports).
     * @private
     */
    getChildrenIds(index, obj, diagram, isNode) {
        const childrenId = [];
        if (obj.annotations) {
            for (const annotation in obj.annotations) {
                if (Object.prototype.hasOwnProperty.call(obj.annotations, annotation)) {
                    if (isNode) {
                        childrenId.push(diagram.nodes[`${index}`].annotations[`${annotation}`].id);
                    }
                    else {
                        childrenId.push(diagram.connectors[`${index}`].annotations[`${annotation}`].id);
                    }
                }
            }
        }
        if (obj.ports) {
            for (const port in obj.ports) {
                if (Object.prototype.hasOwnProperty.call(obj.ports, port)) {
                    if (isNode) {
                        childrenId.push(diagram.nodes[`${index}`].ports[`${port}`].id);
                    }
                    else {
                        childrenId.push(diagram.connectors[`${index}`].ports[`${port}`].id);
                    }
                }
            }
        }
        return childrenId;
    }
    /**
     * Clones connector-specific objects (decorators and segments) for serialization.
     * Ensures that complex nested properties like gradients and segment arrays
     * are properly cloned before being serialized for remote transmission.
     * @param {ConnectorModel} obj - The connector model to process.
     * @returns {void}
     * @private
     */
    cloneAsObject(obj) {
        if (obj.sourceDecorator && obj.sourceDecorator.style && obj.sourceDecorator.style.gradient) {
            this.cloneGradientStops(obj.sourceDecorator.style.gradient);
        }
        if (obj.targetDecorator && obj.targetDecorator.style && obj.targetDecorator.style.gradient) {
            this.cloneGradientStops(obj.targetDecorator.style.gradient);
        }
        if (obj.segments) {
            const clonedValue = cloneObject(obj.segments);
            obj.segments = clonedValue;
        }
    }
    /**
     * Deep clones gradient stop objects within a gradient definition.
     * Gradient stops contain color and offset information that must be
     * individually cloned to avoid reference sharing during serialization.
     * @param {GradientModel} gradient - The gradient model containing stops to clone.
     * @returns {void}
     * @private
     */
    cloneGradientStops(gradient) {
        if (gradient && gradient.stops) {
            for (let i = 0; i < gradient.stops.length; i++) {
                const stop = cloneObject(gradient.stops[parseInt(i.toString(), 10)]);
                gradient.stops[parseInt(i.toString(), 10)] = stop;
            }
        }
    }
    /**
     * Gets the affected objects for a change, expanding selection/undo/redo contexts.
     * @param {IHistoryChangeArgs} args - The history change event arguments.
     * @param {(NodeModel | ConnectorModel | SelectorModel | DiagramModel | ShapeAnnotationModel | PathAnnotationModel | PointPortModel)} undoObject - The object state for undo operations.
     * @param {(NodeModel | ConnectorModel | SelectorModel | DiagramModel)} redoObject - The object state for redo operations.
     * @param {Diagram} diagram - The Diagram instance against which the change is applied.
     * @returns {(NodeModel | ConnectorModel)[]} An array of affected NodeModel or ConnectorModel objects.
     * @private
     */
    getChangedObjects(args, undoObject, redoObject, diagram) {
        const isUndoOrRedo = args.action === 'Undo' ||
            args.action === 'Redo';
        if (!isUndoOrRedo) {
            return args.source;
        }
        let historyObject = undefined;
        if (args.action === 'Undo') {
            const undoHasId = !!(undoObject && undoObject.id &&
                diagram.getObject(undoObject.id));
            const undoIsSelector = !!(undoObject &&
                undoObject.nodes &&
                undoObject.connectors);
            const isValidUndo = undoHasId || undoIsSelector;
            historyObject = isValidUndo ? undoObject : undefined;
        }
        else {
            const redoHasId = !!(redoObject && redoObject.id &&
                diagram.getObject(redoObject.id));
            const redoIsSelector = !!(redoObject &&
                redoObject.nodes &&
                redoObject.connectors);
            const isValidRedo = redoHasId || redoIsSelector;
            historyObject = isValidRedo ? redoObject : undefined;
        }
        const selector = historyObject;
        if (selector && selector.nodes && selector.connectors) {
            const objects = [];
            objects.push(...(selector.nodes));
            objects.push(...(selector.connectors));
            return objects;
        }
        else if (historyObject && historyObject) {
            return [historyObject];
        }
        return args.source;
    }
    /**
     * Classifies changed objects by type and enriches them with collaboration metadata.
     * @param {Array.<(NodeModel|ConnectorModel)>|null|undefined} changedObjects - The list of node/connector models that were changed.
     * @param {HistoryEntry} historyEntry - The history entry describing the change.
     * @returns {{nodes: Node[], connectors: Connector[]}} An object with `nodes` and `connectors` arrays ready for serialization.
     * @private
     */
    categorizeAndEnrichObjects(changedObjects, historyEntry) {
        const nodes = [];
        const connectors = [];
        if (!changedObjects || changedObjects.length === 0 || historyEntry.type === 'PropertyChanged') {
            return { nodes: nodes, connectors: connectors };
        }
        for (const obj of changedObjects) {
            if (obj.nodes) {
                for (const node of obj.nodes) {
                    const nodeTyped = node;
                    this.updateNodeAndConnectors(nodeTyped, nodes, connectors);
                }
            }
            else if (obj.connectors) {
                for (const connector of obj.connectors) {
                    const connectorTyped = connector;
                    this.updateNodeAndConnectors(connectorTyped, nodes, connectors);
                }
            }
            else {
                this.updateNodeAndConnectors(obj, nodes, connectors);
            }
        }
        return { nodes: nodes, connectors: connectors };
    }
    /**
     * Categorizes and clones a single diagram object into the appropriate collection.
     * Determines if the object is a node or connector based on its properties,
     * adds parent information, and clones it for serialization.
     * @param {NodeModel | ConnectorModel} obj - The diagram object to categorize.
     * @param {Node[]} nodes - The collection of nodes to append to.
     * @param {Connector[]} connectors - The collection of connectors to append to.
     * @returns {void}
     * @private
     */
    updateNodeAndConnectors(obj, nodes, connectors) {
        if (obj.offsetX !== undefined) {
            obj.addInfo = { 'parentId': obj.parentId };
            nodes.push(cloneObject(obj));
        }
        else if (obj.sourcePoint && obj.targetPoint) {
            obj.addInfo = { 'parentId': obj.parentId };
            connectors.push(cloneObject(obj));
        }
    }
    /**
     * Validates a diagram changes object to ensure it has the required structure.
     * Performs security validation on incoming collaboration data to prevent
     * malicious payloads from corrupting the diagram state or causing runtime errors.
     * @param {unknown} data - The parsed JSON data to validate.
     * @returns {DiagramChanges | null} The validated DiagramChanges object, or null if invalid.
     * @private
     */
    validateDiagramChanges(data) {
        if (typeof data !== 'object' || data === null) {
            return null;
        }
        const changes = data;
        // Validate required fields
        if (!changes.entryType || typeof changes.entryType !== 'string') {
            return null;
        }
        // Validate arrays if present
        if (changes.modifiedNodes !== undefined && changes.modifiedNodes !== null && !Array.isArray(changes.modifiedNodes)) {
            return null;
        }
        if (changes.modifiedConnectors !== undefined && changes.modifiedConnectors !== null && !Array.isArray(changes.modifiedConnectors)) {
            return null;
        }
        return changes;
    }
    /**
     * Applies a collection of remote change operations to the current diagram.
     *
     * @param {string[]} modifiedOperations - An array of serialized `DiagramChanges` JSON strings
     *                                        received from remote collaborators.
     * @param {Diagram} diagram - The target `Diagram` instance to which the remote updates
     *                             should be applied.
     * @returns {void}
     * @private
     */
    applyRemoteChanges(modifiedOperations, diagram) {
        if (!diagram.undoRedoModule) {
            return;
        }
        if (modifiedOperations.length > 0) {
            diagram.realActions |= RealAction.PreventHistoryEntryForCollaboration;
            const previous = diagram.getProtectPropertyChangeValue();
            diagram.protectPropertyChange(true);
            modifiedOperations.forEach((data) => {
                try {
                    const parsedData = this.validateDiagramChanges(JSON.parse(data));
                    if (parsedData) {
                        this.updateDiagram(parsedData, diagram);
                    }
                    else {
                        console.warn('[WARNING] :: Invalid diagram change payload received');
                    }
                }
                catch (error) {
                    console.warn('[WARNING] :: Failed to parse or apply diagram changes:', error);
                }
            });
            diagram.realActions &= ~RealAction.PreventHistoryEntryForCollaboration;
            diagram.protectPropertyChange(previous);
        }
    }
    /**
     * Updates the remote changes into the current diagram.
     *
     * @param {DiagramChanges} parsedData - The parsed `DiagramChanges` payload to apply.
     * @param {Diagram} diagram - The `Diagram` instance to update.
     * @returns {void}
     * @private
     */
    updateDiagram(parsedData, diagram) {
        const modifiedNodes = parsedData.modifiedNodes;
        const modifiedConnectors = parsedData.modifiedConnectors;
        const historyEntryType = parsedData.entryType;
        const historyAction = parsedData.historyAction;
        const historyEntryChangeType = parsedData.collectionChangedAction;
        const propertyChanges = parsedData.propertyChanges;
        const actionTrigger = parsedData.actionTrigger;
        const entry = parsedData.entry;
        if (actionTrigger === 'Undo' || actionTrigger === 'Redo') {
            diagram.diagramActions |= DiagramAction.UndoRedo;
        }
        const { type: adjustedHistoryType, change: adjustedChangeType } = this.getAdjustedHistoryTypes(historyEntryType, historyEntryChangeType, actionTrigger);
        if (historyEntryType === 'PropertyChanged') {
            if (actionTrigger !== 'Undo') {
                this.processPropertyChanges(propertyChanges.sourceIds, propertyChanges.oldValue, propertyChanges.newValue, propertyChanges.childrenId, diagram);
            }
            else {
                this.processPropertyChanges(propertyChanges.sourceIds, propertyChanges.newValue, propertyChanges.oldValue, propertyChanges.childrenId, diagram);
            }
        }
        if ((Array.isArray(modifiedNodes) && modifiedNodes.length > 0) || (propertyChanges && historyEntryType === 'LanePositionChanged')) {
            this.processNodeChangesAsync(modifiedNodes, propertyChanges, adjustedHistoryType, entry, adjustedChangeType, historyAction, diagram);
        }
        else if (Array.isArray(modifiedConnectors) && modifiedConnectors.length > 0) {
            this.processConnectorChangesAsync(modifiedConnectors, propertyChanges, adjustedHistoryType, adjustedChangeType, diagram);
        }
        if (actionTrigger === 'Undo' || actionTrigger === 'Redo') {
            diagram.diagramActions &= ~DiagramAction.UndoRedo;
        }
    }
    /**
     * Applies property-level updates from a remote payload into the given diagram.
     *
     * @param {string[]} stringids - Array of object ids (nodes/connectors) to update.
     * @param {DiagramModel} oldValues - DiagramModel containing the old state for undo comparisons.
     * @param {DiagramModel} newValues - DiagramModel containing the new state to apply.
     * @param {string[]} childrenId - Optional list of child ids (annotations/ports) to map nested updates.
     * @param {Diagram} diagram - The Diagram instance that will be updated.
     * @returns {void}
     * @private
     */
    processPropertyChanges(stringids, oldValues, newValues, childrenId, diagram) {
        if (newValues.nodes) {
            const newValue = [];
            const oldValue = [];
            const newData = newValues.nodes;
            const oldData = oldValues.nodes;
            for (const value in newData) {
                if (Object.prototype.hasOwnProperty.call(newData, value)) {
                    newValue.push(newData[`${value}`]);
                    oldValue.push(oldData[`${value}`]);
                }
            }
            for (let indexId = 0; indexId < stringids.length; indexId++) {
                const obj = diagram.nameTable[`${stringids[parseInt(indexId.toString(), 10)]}`];
                if (newValue[parseInt(indexId.toString(), 10)].annotations && childrenId && childrenId.length > 0) {
                    let index = 0;
                    for (const annotationIndex in newValue[parseInt(indexId.toString(), 10)].annotations) {
                        if (Object.prototype.hasOwnProperty.call(newValue[parseInt(indexId.toString(), 10)].annotations, annotationIndex)) {
                            for (const current of obj.annotations) {
                                if (current.id === childrenId[parseInt(indexId.toString(), 10)]) {
                                    this.updatePublicProperties(current, newValue[parseInt(indexId.toString(), 10)].annotations[`${annotationIndex}`]);
                                    index++;
                                    break;
                                }
                            }
                        }
                    }
                }
                if (newValue[parseInt(indexId.toString(), 10)].ports && childrenId && childrenId.length > 0) {
                    let index = 0;
                    for (const newPort in newValue[parseInt(indexId.toString(), 10)].ports) {
                        if (Object.prototype.hasOwnProperty.call(newValue[parseInt(indexId.toString(), 10)].ports, newPort)) {
                            for (const current of obj.ports) {
                                if (current.id === childrenId[parseInt(index.toString(), 10)]) {
                                    this.updatePublicProperties(current, newValue[parseInt(indexId.toString(), 10)].ports[`${newPort}`]);
                                    index++;
                                    break;
                                }
                            }
                        }
                    }
                }
                this.updatePublicProperties(obj, newValue[parseInt(indexId.toString(), 10)]);
                if (newValue[parseInt(indexId.toString(), 10)].style && newValue[parseInt(indexId.toString(), 10)].style.gradient) {
                    const dstStops = newValue[parseInt(indexId.toString(), 10)].style.gradient.stops;
                    const srcStops = obj.style.gradient.stops;
                    for (let stopIndex = 0; stopIndex < dstStops.length; stopIndex++) {
                        dstStops[parseInt(stopIndex.toString(), 10)] = srcStops[parseInt(stopIndex.toString(), 10)];
                    }
                    diagram.updateGradient(newValue[parseInt(indexId.toString(), 10)], oldValue[parseInt(indexId.toString(), 10)], obj);
                    obj.oldGradientValue = cloneObject(newValue[parseInt(indexId.toString(), 10)].style.gradient);
                }
                const _oldNode = oldValue[parseInt(indexId.toString(), 10)];
                const _newNode = newValue[parseInt(indexId.toString(), 10)];
                diagram.nodePropertyChange(obj, _oldNode, _newNode, undefined, true, true);
            }
        }
        if (newValues.connectors) {
            const newValue = [];
            const oldValue = [];
            const newData = newValues.connectors;
            const oldData = oldValues.connectors;
            for (const value in newData) {
                if (Object.prototype.hasOwnProperty.call(newData, value)) {
                    newValue.push(newData[`${value}`]);
                    oldValue.push(oldData[`${value}`]);
                }
            }
            for (let i = 0; i < stringids.length; i++) {
                const obj = diagram.nameTable[`${stringids[parseInt(i.toString(), 10)]}`];
                if (newValue[parseInt(i.toString(), 10)].annotations &&
                    childrenId &&
                    childrenId.length > 0) {
                    let index = 0;
                    for (const annotationIndex in newValue[parseInt(i.toString(), 10)].annotations) {
                        if (Object.prototype.hasOwnProperty.call(newValue[parseInt(i.toString(), 10)].annotations, annotationIndex)) {
                            for (const current of obj.annotations) {
                                if (current.id === childrenId[parseInt(index.toString(), 10)]) {
                                    this.updatePublicProperties(current, newValue[parseInt(index.toString(), 10)].annotations[`${annotationIndex}`]);
                                    index++;
                                    break;
                                }
                            }
                        }
                    }
                }
                if (newValue[parseInt(i.toString(), 10)].ports &&
                    childrenId &&
                    childrenId.length > 0) {
                    let index = 0;
                    for (const newPort in newValue[parseInt(i.toString(), 10)].ports) {
                        if (Object.prototype.hasOwnProperty.call(newValue[parseInt(i.toString(), 10)].ports, newPort)) {
                            for (const port of obj.ports) {
                                if (port.id === childrenId[parseInt(index.toString(), 10)]) {
                                    this.updatePublicProperties(port, newValue[parseInt(i.toString(), 10)].ports[`${newPort}`]);
                                    index++;
                                    break;
                                }
                            }
                        }
                    }
                }
                const connectorNew = newValue[parseInt(i.toString(), 10)];
                this.updatePublicProperties(obj, connectorNew);
                const _oldConnector = oldValue[`${i}`];
                const _newConnector = newValue[`${i}`];
                diagram.connectorPropertyChange(obj, _oldConnector, _newConnector, undefined, true);
            }
        }
    }
    /**
     * Copies public properties from `target` onto `source` with security protections.
     *
     * Performs a shallow copy for primitives and arrays, and recursively copies
     * plain object properties. Includes protection against circular references
     * and prototype pollution attacks.
     *
     * @param {Model} source - The existing object that will receive updated properties.
     * @param {Model} target - The object providing new property values.
     * @returns {void}
     * @private
     */
    updatePublicProperties(source, target) {
        if (!source || typeof source !== 'object') {
            return;
        }
        const targetKeys = Object.keys(target);
        const sourceAll = source;
        const targetAll = target;
        for (const key of targetKeys) {
            // skip updating nested collections; handled elsewhere or intentionally omitted
            if (key === 'annotations' || key === 'ports') {
                continue;
            }
            // handle shape type changes: if a property is a ShapeModel with differing type, replace it entirely
            const isShape = targetAll[`${key}`];
            if (isShape && isShape.type) {
                const srcShape = sourceAll[`${key}`];
                if (srcShape && srcShape.type !== isShape.type) {
                    sourceAll[`${key}`] = targetAll[`${key}`];
                    continue;
                }
            }
            const isTargetPlainObject = targetAll[`${key}`] && typeof targetAll[`${key}`] === 'object' && !Array.isArray(targetAll[`${key}`]);
            if (isTargetPlainObject) {
                this.updatePublicProperties(sourceAll[`${key}`], targetAll[`${key}`]);
            }
            else {
                sourceAll[`${key}`] = targetAll[`${key}`];
            }
        }
    }
    /**
     * Processes node-level history changes and delegates to the appropriate
     * undo/redo or collection handlers on the diagram.
     *
     * @param {Node[]} nodes - Array of `Node` instances from the remote payload.
     * @param {PropertyChangedAction} propertyChanges - Optional property-change metadata for nested updates.
     * @param {EntryType} historyType - The history entry type being applied (e.g., PositionChanged).
     * @param {ExtendedHistoryEntry} entry - The extended history entry with additional context.
     * @param {EntryChangeType} changeType - The collection change type (Insert/Remove) when relevant.
     * @param {DiagramHistoryAction} historyAction - The `DiagramHistoryAction` associated with the entry.
     * @param {Diagram} diagram - The `Diagram` instance to update.
     * @returns {void}
     * @private
     */
    processNodeChangesAsync(nodes, propertyChanges, historyType, entry, changeType, historyAction, diagram) {
        for (const nodeObj of nodes) {
            const objectToUpdate = diagram.getObject(nodeObj.id);
            const previous = diagram.getProtectPropertyChangeValue();
            diagram.protectPropertyChange(true);
            if (!objectToUpdate && historyType !== 'Group' && historyType !== 'CollectionChanged' && historyType !== 'LanePositionChanged'
                && historyType !== 'PhaseCollectionChanged' && historyType !== 'LaneCollectionChanged') {
                continue;
            }
            diagram.isCollaborativeContainerChanges =
                (nodeObj && nodeObj.shape && nodeObj.shape.type === 'Container') ||
                    (!!(nodeObj && nodeObj.parentId) &&
                        diagram &&
                        diagram.nameTable &&
                        diagram.nameTable[nodeObj.parentId] &&
                        diagram.nameTable[nodeObj.parentId].shape &&
                        diagram.nameTable[nodeObj.parentId].shape.type === 'Container');
            try {
                switch (historyType) {
                    case 'Align':
                    case 'Distribute':
                    case 'PositionChanged': {
                        diagram.undoRedoModule.positionChanged(nodeObj, diagram);
                        break;
                    }
                    case 'SizeChanged':
                    case 'Sizing': {
                        diagram.undoRedoModule.nodeSizeChange(nodeObj, diagram, entry);
                        break;
                    }
                    case 'RotationChanged': {
                        diagram.undoRedoModule.rotateNode({ nodes: nodes }, nodeObj, diagram, entry, 'undo');
                        break;
                    }
                    case 'LabelCollectionChanged': {
                        if (propertyChanges) {
                            const annotation = propertyChanges.newObjectValue;
                            if (annotation) {
                                diagram.undoRedoModule.labelCollectionChanged(nodeObj, annotation, changeType, diagram);
                            }
                        }
                        break;
                    }
                    case 'ColumnWidthChanged': {
                        this.recordGridSizeChanged(nodeObj, diagram, false, false);
                        break;
                    }
                    case 'RowHeightChanged': {
                        this.recordGridSizeChanged(nodeObj, diagram, false, true);
                        break;
                    }
                    case 'CollectionChanged': {
                        diagram.undoRedoModule.collectionChanged(nodeObj, changeType, null, diagram);
                        break;
                    }
                    case 'PhaseCollectionChanged':
                    case 'LaneCollectionChanged': {
                        if (propertyChanges) {
                            diagram.undoRedoModule.laneOrPhaseCollectionChanged(changeType, nodeObj, propertyChanges.newObjectValue, diagram);
                        }
                        break;
                    }
                    case 'ChildCollectionChanged': {
                        diagram.isCollaborativeContainerChanges = true;
                        diagram.undoRedoModule.childCollectionChanged(nodeObj, diagram, false, historyAction);
                        break;
                    }
                    case 'PortCollectionChanged': {
                        if (changeType === 'Remove') {
                            diagram.removePorts(nodeObj, [propertyChanges.newObjectValue]);
                        }
                        else {
                            diagram.addPorts(nodeObj, [propertyChanges.newObjectValue]);
                        }
                        break;
                    }
                    case 'AnnotationPropertyChanged': {
                        this.annotationPropertyChange(nodeObj, propertyChanges.objectId, diagram);
                        break;
                    }
                    case 'Group': {
                        diagram.add(nodeObj);
                        break;
                    }
                    case 'UnGroup': {
                        diagram.commandHandler.unGroup(nodeObj);
                        break;
                    }
                    case 'SendBackward': {
                        diagram.commandHandler.sendBackward(objectToUpdate);
                        break;
                    }
                    case 'SendToBack': {
                        diagram.commandHandler.sendToBack(objectToUpdate);
                        break;
                    }
                    case 'BringToFront': {
                        diagram.commandHandler.bringToFront(objectToUpdate);
                        break;
                    }
                    case 'SendForward': {
                        diagram.commandHandler.sendForward(objectToUpdate);
                        break;
                    }
                    case 'AddChildToGroupNode': {
                        diagram.addChildToGroup(objectToUpdate, propertyChanges.objectId);
                        break;
                    }
                    case 'RemoveChildFromGroupNode': {
                        diagram.removeChildFromGroup(objectToUpdate, propertyChanges.objectId);
                        break;
                    }
                }
            }
            finally {
                diagram.isCollaborativeContainerChanges = false;
                diagram.protectPropertyChange(previous);
            }
        }
        if (propertyChanges && historyType === 'LanePositionChanged') {
            const positionChangedArgs = propertyChanges.newObjectValue;
            if (positionChangedArgs.source) {
                const parent = diagram.nameTable[positionChangedArgs.source.parentId];
                if (parent && parent.shape.type === 'SwimLane') {
                    laneInterChanged(diagram, positionChangedArgs.target, positionChangedArgs.source);
                    diagram.clearSelection();
                }
            }
        }
    }
    /**
     * Applies annotation property changes from remote updates.
     * Finds the specified annotation on the object and triggers a property change
     * event with old and new values to update the visual state.
     * @param {Node | Connector} obj - The node or connector containing the annotation.
     * @param {string} annotationId - The unique identifier of the annotation to update.
     * @param {Diagram} diagram - The diagram instance.
     * @returns {void}
     * @private
     */
    annotationPropertyChange(obj, annotationId, diagram) {
        const oldElement = findAnnotation(obj, annotationId);
        const undoChanges = diagram.commandHandler.getAnnotationChanges(diagram.nameTable[obj.id], oldElement);
        if (obj.offsetX) {
            diagram.nodePropertyChange(diagram.nameTable[obj.id], {}, undoChanges);
        }
        else {
            diagram.connectorPropertyChange(diagram.nameTable[obj.id], {}, undoChanges);
        }
    }
    /**
     * Records and applies grid size changes for swimlane child elements.
     *
     * This method maps a child node's size differences back to its parent
     * swimlane's grid and triggers layout/arrange updates so the visual
     * swimlane reflects the resized phase/row/column.
     *
     * @param {Node} nodeObj - The node whose size changed (typically a swimlane phase or row child).
     * @param {Diagram} diagram - The Diagram instance containing the swimlane and grid.
     * @param {boolean} isRedo - Whether this change is being applied during a redo operation.
     * @param {boolean} isRow - True when updating a row height; false when updating a column width.
     * @returns {void}
     * @private
     */
    recordGridSizeChanged(nodeObj, diagram, isRedo, isRow) {
        const obj = nodeObj;
        if (obj.parentId) {
            const swimlane = diagram.nameTable[obj.parentId];
            const actualObject = diagram.nameTable[obj.id];
            const x = swimlane.wrapper.bounds.x;
            const y = swimlane.wrapper.bounds.y;
            if (swimlane.shape.type === 'SwimLane') {
                const grid = swimlane.wrapper.children[0];
                const padding = swimlane.shape.padding;
                let isUndoRedo = false;
                if (diagram.diagramActions & DiagramAction.UndoRedo) {
                    isUndoRedo = true;
                }
                //Swimlane break - Phase collapsed upon serialization after resizing and undo-redo.
                let widthDiff = 0;
                let heightDiff = 0;
                if (obj.wrapper && obj.wrapper.actualSize && actualObject.wrapper && actualObject.wrapper.actualSize) {
                    widthDiff = obj.wrapper.actualSize.width - actualObject.wrapper.actualSize.width;
                    heightDiff = obj.wrapper.actualSize.height - actualObject.wrapper.actualSize.height;
                }
                updateSwimLaneObject(diagram, actualObject, swimlane, obj, widthDiff, heightDiff);
                if (isRow) {
                    grid.updateRowHeight(obj.rowIndex, obj.wrapper.actualSize.height, true, padding, isUndoRedo);
                    swimlane.height = swimlane.wrapper.height = grid.height;
                }
                else {
                    grid.updateColumnWidth(obj.columnIndex, obj.wrapper.actualSize.width, true, padding, isUndoRedo);
                    swimlane.width = swimlane.wrapper.width = grid.width;
                    if (obj.isPhase) {
                        actualObject.maxWidth = actualObject.wrapper.maxWidth = obj.wrapper.actualSize.width;
                    }
                }
                swimLaneMeasureAndArrange(swimlane);
                if (swimlane.shape.orientation === 'Horizontal') {
                    updatePhaseMaxWidth(swimlane, diagram, actualObject.wrapper, actualObject.columnIndex);
                }
                updateHeaderMaxWidth(diagram, swimlane);
                const tx = x - swimlane.wrapper.bounds.x;
                const ty = y - swimlane.wrapper.bounds.y;
                diagram.drag(swimlane, tx, ty);
                diagram.clearSelection();
                diagram.updateDiagramObject(swimlane);
            }
        }
    }
    /**
     * Processes connector-level history changes and delegates to the appropriate
     * handlers (segment/connection/collection/port/label updates and ordering
     * commands) on the diagram.
     *
     * @param {Connector[]} connectors - Array of `Connector` instances from the remote payload.
     * @param {PropertyChangedAction} propertyChanges - Optional property-change metadata for nested updates.
     * @param {EntryType} historyType - The history entry type being applied (e.g., SegmentChanged).
     * @param {EntryChangeType} changeType - The collection change type (Insert/Remove) when relevant.
     * @param {Diagram} diagram - The `Diagram` instance to update.
     * @returns {void}
     * @private
     */
    processConnectorChangesAsync(connectors, propertyChanges, historyType, changeType, diagram) {
        for (const connectorObj of connectors) {
            const objectToUpdate = diagram.getObject(connectorObj.id);
            if (!objectToUpdate && historyType !== 'CollectionChanged') {
                continue;
            }
            switch (historyType) {
                case 'PositionChanged':
                case 'SizeChanged':
                case 'RotationChanged': {
                    diagram.undoRedoModule.segmentChanged(connectorObj, diagram);
                    diagram.undoRedoModule.connectionChanged(connectorObj, diagram);
                    break;
                }
                case 'ConnectionChanged': {
                    diagram.undoRedoModule.recordConnectionChanged(connectorObj, diagram);
                    break;
                }
                case 'SegmentChanged': {
                    diagram.undoRedoModule.segmentChanged(connectorObj, diagram);
                    break;
                }
                case 'LabelCollectionChanged': {
                    if (propertyChanges) {
                        const annotation = propertyChanges.newObjectValue;
                        if (annotation) {
                            diagram.undoRedoModule.labelCollectionChanged(connectorObj, annotation, changeType, diagram);
                        }
                    }
                    break;
                }
                case 'PortCollectionChanged': {
                    if (propertyChanges) {
                        const port = propertyChanges.newObjectValue;
                        if (port) {
                            diagram.undoRedoModule.portCollectionChanged(connectorObj, port, changeType, diagram);
                        }
                    }
                    break;
                }
                case 'CollectionChanged': {
                    diagram.undoRedoModule.collectionChanged(connectorObj, changeType, null, diagram);
                    break;
                }
                case 'SendBackward': {
                    diagram.commandHandler.sendBackward(objectToUpdate);
                    break;
                }
                case 'SendToBack': {
                    diagram.commandHandler.sendToBack(objectToUpdate);
                    break;
                }
                case 'BringToFront': {
                    diagram.commandHandler.bringToFront(objectToUpdate);
                    break;
                }
                case 'SendForward': {
                    diagram.commandHandler.sendForward(objectToUpdate);
                    break;
                }
            }
        }
    }
    /**
     * Adjusts history type and collection change for Undo operations.
     *
     * @param {EntryType} type - Original history type.
     * @param {EntryChangeType} change - Original collection change.
     * @param {HistoryChangeAction} [trigger] - Trigger action (e.g., 'Undo').
     * @returns {{type: EntryType, change: EntryChangeType}} Adjusted type and change.
     * @private
     */
    getAdjustedHistoryTypes(type, change, trigger) {
        if (trigger === 'Undo') {
            const adjustedChange = change === 'Insert'
                ? 'Remove'
                : 'Insert';
            const adjustedTypeMap = {
                Group: 'UnGroup',
                UnGroup: 'Group',
                AddChildToGroupNode: 'RemoveChildFromGroupNode',
                RemoveChildFromGroupNode: 'AddChildToGroupNode'
            };
            let adjustedType = type;
            if (Object.prototype.hasOwnProperty.call(adjustedTypeMap, String(type))) {
                adjustedType = adjustedTypeMap[String(type)];
            }
            return { type: adjustedType, change: adjustedChange };
        }
        return { type, change };
    }
    /**
     * To destroy the collaborative module
     *
     * @returns {void}
     * @private
     */
    destroy() {
        this.collaborativeCollection = [];
        this.isGroupAction = false;
    }
    /**
     * Returns the module name.
     *
     * @returns {string} The module name for this class.
     * @private
     */
    getModuleName() {
        return 'DiagramCollaboration';
    }
}
